import { Box, Stack } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { observer } from "mobx-react";
import React, { useMemo } from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../Stores/LLMClassifierBuilderStore";
import { ModelVisibility } from "models/LLMs/ModelVisibility";

interface VisibilityOption {
    value: ModelVisibility;
    label: string;
    description: string;
}

const allOptions: VisibilityOption[] = [
    {
        value: ModelVisibility.Anyone,
        label: "Anyone",
        description:
            "Anyone with permission to see model results will see the results of this model.",
    },
    {
        value: ModelVisibility.AuthenticxOnly,
        label: "Only Authenticx team members",
        description:
            "When enabled, only Authenticx team members can view this model's results. Use this while validating a model before releasing it to users.",
    },
    {
        value: ModelVisibility.SpecificRoles,
        label: "Only specific roles",
        description:
            "Only users with the selected roles will be able to view this model's results.",
    },
];

export const ModelVisibilitySelector = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);
    const isAuthcx = store.authStore.isAuthcxUser();
    const isLockedAuthenticxVisibility =
        !isAuthcx && store.visibility === ModelVisibility.AuthenticxOnly;
    const selectedOption =
        allOptions.find((o) => o.value === store.visibility) ?? allOptions[0];

    const roleOptions = useMemo(
        () =>
            store.availableRoles.map((r) => ({
                value: r.id,
                label: r.name,
            })),
        [store.availableRoles],
    );

    const selectedRoles = useMemo(
        () =>
            roleOptions.filter((r) => store.selectedRoleIds.includes(r.value)),
        [roleOptions, store.selectedRoleIds],
    );

    return (
        <Stack direction="row" alignItems="start" columnGap={2}>
            <Box width="50%">
                <AcxSelectSingle
                    fullWidth
                    id="model-visibility"
                    options={allOptions}
                    valueField="value"
                    labelField="label"
                    inputLabel="Choose who can view this model's results after it's
                    published:"
                    defaultValue={selectedOption}
                    isDisabled={isLockedAuthenticxVisibility}
                    helperText={
                        isLockedAuthenticxVisibility
                            ? "Only Authenticx team members can change a model that is currently Authenticx-only."
                            : undefined
                    }
                    isOptionDisabled={(option: VisibilityOption) =>
                        !isAuthcx &&
                        option.value === ModelVisibility.AuthenticxOnly
                    }
                    getOptionTooltip={(option: VisibilityOption) =>
                        !isAuthcx &&
                        option.value === ModelVisibility.AuthenticxOnly
                            ? "Only Authenticx team members can select this visibility."
                            : undefined
                    }
                    disabledOptionTooltipText="Only Authenticx team members can select this visibility."
                    onChange={(option: VisibilityOption) => {
                        store.setVisibility(option.value);
                    }}
                />
            </Box>
            {store.visibility === ModelVisibility.SpecificRoles && (
                <Box width="50%">
                    <AcxSelectMulti
                        fullWidth
                        id="model-visibility-roles"
                        inputLabel="Allowed roles"
                        options={roleOptions}
                        valueField="value"
                        labelField="label"
                        defaultValue={selectedRoles}
                        onChange={(
                            selected:
                                | { value: string; label: string }[]
                                | undefined,
                        ) => {
                            store.setSelectedRoleIds(
                                selected?.map((s) => s.value) ?? [],
                            );
                        }}
                        isLoading={store.availableRoles.length === 0}
                        containerHeight="fit-content"
                        maxContainerHeight="96px"
                    />
                </Box>
            )}
        </Stack>
    );
});
